<template>
  <SSection :has-separator="false" :mobile-full="true">
    <div class="relative z-0">
      <CottageFavorite :item="item" />
      <div v-if="!isMobile">
        <div data-test="desktopGallery" class="aspect-w-9 aspect-h-3">
          <div class="grid gap-1 grid-cols-4 grid-rows-2 h-full">
            <div
              v-for="(img, index) in desktopPhotoItems"
              :key="index"
              class="bg-gray-100 relative"
              :class="index == 0 ? 'row-span-2 col-span-2' : ''"
            >
              <button
                class="cursor-pointer w-full h-full relative transition ease-in-out duration-300 hover:opacity-80 focus-visible:ring-primary-400 focus-visible:ring-opacity-50 focus-visible:ring-4 focus-visible:outline-0"
                :aria-label="
                  t('views.cottage.gallery.a11y.openGallery', index + 1)
                "
                @click="openPhotoModal(index)"
              >
                <LazyVisibleTLazyimg
                  hydrate-on-visible
                  :src="img.url"
                  classes="block w-full h-full"
                  classes-img="block w-full h-full object-cover rounded-lg"
                  alt=""
                  :preload="index === 0 ? { fetchPriority: 'high' } : false"
                  :loading="index === 0 ? 'eager' : 'lazy'"
                />
              </button>
            </div>
          </div>
        </div>

        <div class="absolute bottom-4 right-4 z-10">
          <div class="mt-4 flex gap-x-2">
            <div>
              <UButton
                data-test="desktopGalleryOpenPhotoModal"
                color="neutral"
                variant="subtle"
                icon="i-heroicons:photo"
                size="sm"
                @click="openPhotoModal()"
              >
                {{ t('views.cottage.gallery.photos', numberOfPhotoItems) }}
              </UButton>
            </div>

            <div v-if="numberOfVideoItems > 0">
              <UButton
                data-test="desktopGalleryOpenVideoLightbox"
                color="neutral"
                variant="subtle"
                icon="i-heroicons:play-circle"
                size="sm"
                @click="openVideoLightbox()"
              >
                {{ t('views.cottage.gallery.videos', numberOfVideoItems) }}
              </UButton>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isMobile">
        <div
          data-test="mobileCarousel"
          class="aspect-w-3 aspect-h-2 bg-gray-100 relative"
        >
          <UCarousel
            ref="carouselRef"
            :items="mobilePhotoItems"
            :ui="fullWidthCarousel"
            class="absolute inset-0 overflow-hidden"
            @select="updateCurrentCarouselPage"
          >
            <template #default="slideItem">
              <div class="relative w-full h-full aspect-w-3 aspect-h-2 m-auto">
                <LazyTLazyimg
                  :src="slideItem.item.url"
                  classes-img="block w-full h-full object-cover"
                  :alt="
                    t(
                      'views.cottage.gallery.a11y.openGallery',
                      slideItem.index + 1,
                    )
                  "
                  draggable="false"
                  :preload="
                    slideItem.index === 0 || slideItem.index === 1
                      ? { fetchPriority: 'high' }
                      : false
                  "
                  :loading="
                    slideItem.index === 0 || slideItem.index === 1
                      ? 'eager'
                      : 'lazy'
                  "
                  @click="openPhotoModal(slideItem.index)"
                />
              </div>
            </template>
          </UCarousel>
        </div>
        <div
          v-if="isMobile"
          data-test="mobileCarouselCurrentImage"
          class="absolute bottom-4 right-4 z-10"
        >
          <span
            class="inline-flex items-center text-sm px-2.5 py-1.5 bg-black/60 text-white font-bold rounded-full"
          >
            {{
              t('views.cottage.gallery.legend', {
                current: currentCarouselPage,
                total: numberOfPhotoItems,
              })
            }}
          </span>
        </div>
      </div>
    </div>

    <TContainer v-if="numberOfVideoItems > 0 && isMobile" class="mt-4">
      <UButton
        data-test="mobileGalleryOpenVideoLightbox"
        block
        color="neutral"
        variant="subtle"
        icon="i-heroicons:play-circle"
        size="sm"
        @click="openVideoLightbox()"
      >
        {{ t('views.cottage.gallery.videos', numberOfVideoItems) }}
      </UButton>
    </TContainer>
  </SSection>

  <UModal
    v-model:open="isPhotoModalOpen"
    fullscreen
    :dismissible="false"
    :modal="false"
    :content="{
      onEscapeKeyDown: escapePhotoModal,
    }"
  >
    <template #body>
      <CottageSectionGalleryMasonry
        :photo-items="photoItems"
        :current-index="currentIndex"
        @open-photo-lightbox="openPhotoLightbox"
      ></CottageSectionGalleryMasonry>
    </template>
  </UModal>
</template>

<script setup lang="ts">
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import type { DataSource } from 'photoswipe/lightbox'
import PhotoSwipeDynamicCaption from 'photoswipe-dynamic-caption-plugin'
import 'photoswipe/style.css'
import 'photoswipe-dynamic-caption-plugin/photoswipe-dynamic-caption-plugin.css'

import type { CottagePage } from '../../entities/CottagePage'
import { useCottageMedia } from '~~/modules/views/cottage/composables/useCottageMedia'

import { fullWidthCarousel } from '~~/modules/design-system/utils/nuxt-ui-config/components/carousel'

// Composables
const { getPhotoItems, getDesktopPhotoItems, getVideoItems } = useCottageMedia()
const { isMobile } = useDevice()

// References
const currentIndex = ref(0)
const currentCarouselPage = ref(1)
const carouselRef = ref()
const isPhotoModalOpen = ref(false)
let photoLightbox: PhotoSwipeLightbox | null = null
let videoLightbox: PhotoSwipeLightbox | null = null

// Props
type itemType = Pick<
  CottagePage,
  'id' | 'media' | 'name' | 'village' | 'province'
>

interface propsInterface {
  item: itemType
}
const props = defineProps<propsInterface>()

// Data
const photoItems = getPhotoItems(props.item.media)
const desktopPhotoItems = getDesktopPhotoItems(props.item.media)
const mobilePhotoItems = ref([photoItems[0], photoItems[1]])
const videoItems = getVideoItems(props.item.media)

// Methods
let loadedImages: boolean = false
const updateCurrentCarouselPage = (index: number) => {
  if (!loadedImages && index !== 0) {
    mobilePhotoItems.value = photoItems
    loadedImages = true
  }
  currentCarouselPage.value = index + 1
}

const openPhotoModal = (indexProvided: number = 0) => {
  currentIndex.value = indexProvided

  isPhotoModalOpen.value = true
}

const escapePhotoModal = () => {
  if (photoLightbox && photoLightbox.pswp) {
    // PhotoSwipe is open, close it manually
    photoLightbox.pswp.close()
  } else {
    // PhotoSwipe is closed, close the modal
    isPhotoModalOpen.value = false
  }
}

const openVideoLightbox = async () => {
  if (videoLightbox) {
    videoLightbox.destroy()
    videoLightbox = null
  }

  const PhotoSwipeLightbox = (await import('photoswipe/lightbox')).default
  const PhotoSwipeDynamicCaption = (
    await import('photoswipe-dynamic-caption-plugin')
  ).default
  await import('photoswipe/style.css')
  const galleryNodeList: DataSource = videoItems.map(video => ({
    html: `<iframe width="100%" height="100%" title="${video.alt}" src="${routesExternal().youtubeVideo({ slug: video.url })}" frameborder="0" allow="autoplay; encrypted-media" allowfullscreen></iframe>`,
    width: 640,
    height: 360,
    description: video.alt,
  }))

  videoLightbox = new PhotoSwipeLightbox({
    dataSource: galleryNodeList,
    mainClass: 'cottage-section-gallery__video-swipe',
    pswpModule: () => import('photoswipe'),
  })

  new PhotoSwipeDynamicCaption(videoLightbox, {
    type: 'below',
    captionContent: (slide: { data: Record<string, unknown> }) =>
      slide.data.description,
  })

  videoLightbox.on('close', () => {
    // PhotoSwipe starts to close, unbind most events here
    if (videoLightbox) {
      videoLightbox.destroy()
      videoLightbox = null
    }
  })

  videoLightbox.init()
  videoLightbox.loadAndOpen(0)
}

const openPhotoLightbox = (index: number) => {
  if (photoLightbox) {
    photoLightbox.destroy()
    photoLightbox = null
  }

  const galleryNodeList: DataSource = photoItems.map(image => ({
    src: image.url,
    width: image.width,
    height: image.height,
    description: image.alt,
  }))

  photoLightbox = new PhotoSwipeLightbox({
    dataSource: galleryNodeList,
    showHideAnimationType: 'fade',
    escKey: false,
    pswpModule: () => import('photoswipe'),
  })

  new PhotoSwipeDynamicCaption(photoLightbox, {
    type: 'below',
    captionContent: (slide: { data: Record<string, unknown> }) =>
      slide.data.description,
  })

  photoLightbox.on('close', () => {
    // PhotoSwipe starts to close, unbind most events here
    if (photoLightbox) {
      photoLightbox.destroy()
      photoLightbox = null
    }
  })

  photoLightbox.init()
  photoLightbox.loadAndOpen(index)
}

// Reactivity
const numberOfPhotoItems = computed(() => {
  return photoItems.length
})

const numberOfVideoItems = computed(() => {
  return videoItems.length
})

watch(() => carouselRef.value?.page, updateCurrentCarouselPage)

// Cleanup on unmount
onUnmounted(() => {
  if (photoLightbox) {
    photoLightbox.destroy()
    photoLightbox = null
  }
  if (videoLightbox) {
    videoLightbox.destroy()
    videoLightbox = null
  }
})
</script>

<style lang="postcss">
.cottage-section-gallery__video-swipe.pswp--touch {
  .pswp__button--arrow {
    visibility: visible;
  }
}
</style>
